'use client';

import Link from 'next/link';
import {
	ArrowRight,
	BarChart3,
	Bell,
	Check,
	ClipboardList,
	ShieldCheck,
	UserSearch,
	Users,
} from 'lucide-react';
import { useAdminPendientes } from '@/components/admin/AdminPendientesProvider';
import { ATAJOS_SISTEMAS, HomeAtajoCard, type HomeAtajo } from './HomeAtajoCard';
import { HomeSectionTitle } from './HomeSectionTitle';
import { Reveal } from './Reveal';

/**
 * Los cuatro destinos que un admin abre desde la portada. Son un ATAJO al panel,
 * no un índice: el catálogo completo vive en `/admin` (`AdminModulesGrid`), y
 * duplicarlo acá lo dejaría desactualizado a la primera. Las etiquetas y los
 * iconos son los mismos de allá a propósito.
 */
const ATAJOS_ADMIN: HomeAtajo[] = [
	{ label: 'Solicitudes', href: '/admin/solicitudes', icon: ClipboardList },
	{ label: 'Alertas del sistema', href: '/admin/alertas', icon: Bell },
	{ label: 'Datos de investigadores', href: '/admin/investigadores', icon: UserSearch },
	{ label: 'Auditoría', href: '/admin/auditoria', icon: BarChart3 },
];

/**
 * Banda de cierre para una cuenta SIN ficha de investigador: hoy, en la
 * práctica, la de un admin de gestión.
 *
 * Existe por una razón de ritmo, no de contenido: sin ella la portada encadenaba
 * la banda verde de "qué encontrarás aquí" con el pie, también verde, y el
 * bloque perdía el respiro blanco que lo separa. La alternativa —dejar el hueco
 * vacío— se veía como un error de carga.
 *
 * Los pendientes NO se piden acá: `AdminPendientesProvider` ya los tiene (está
 * montado en el layout raíz, así que también cubre la portada) y solo consulta
 * cuando la sesión es admin. Un `null` no rompe nada: `PendingBadge` no pinta
 * nada con 0.
 *
 * Para una cuenta de gestión que NO es admin, la cara cambia entera: mostrarle
 * atajos a `/admin/*` sería ofrecerle puertas que la API le va a cerrar con 403.
 */
export function HomeGestionAtajos({ esAdmin }: { esAdmin: boolean }) {
	const { resumen } = useAdminPendientes();

	if (!esAdmin) {
		return (
			<div className="bg-white px-6 pb-20 pt-16 sm:px-12">
				<Reveal>
					<div className="grid gap-10 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)] lg:items-center lg:gap-14">
						<div>
							<HomeSectionTitle icon={Users}>Tu cuenta</HomeSectionTitle>
							<p className="mt-6 text-[15px] leading-relaxed text-black/[0.66]">
								Esta cuenta no tiene una ficha de investigador vinculada, así que no hay
								un perfil que completar. Desde acá puedes seguir al directorio o a los
								sistemas de la Vicerrectoría.
							</p>
							<div className="mt-7">
								<Link
									href="/investigadores"
									className="inline-flex items-center gap-2 rounded-[10px] bg-[#233a4d] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#233a4d]/90"
								>
									Ver el directorio
									<ArrowRight size={16} aria-hidden />
								</Link>
							</div>
						</div>

						<div className="flex flex-col gap-2.5">
							{ATAJOS_SISTEMAS.map((atajo) => (
								<HomeAtajoCard key={atajo.label} {...atajo} />
							))}
						</div>
					</div>
				</Reveal>
			</div>
		);
	}

	const solicitudes = resumen?.solicitudes ?? 0;
	const alertas = resumen?.alertas ?? 0;
	const total = solicitudes + alertas;

	// Solo se nombra lo que efectivamente tiene pendientes: "1 solicitud y 0
	// alertas" es ruido, y en plural o singular según corresponda.
	const partes = [
		solicitudes > 0 ? `${solicitudes} ${solicitudes === 1 ? 'solicitud' : 'solicitudes'}` : null,
		alertas > 0 ? `${alertas} ${alertas === 1 ? 'alerta' : 'alertas'}` : null,
	].filter(Boolean) as string[];

	return (
		<div className="bg-white px-6 pb-20 pt-16 sm:px-12">
			<Reveal>
				<div className="grid gap-10 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)] lg:items-center lg:gap-14">
					<div>
						<HomeSectionTitle icon={ShieldCheck}>Panel de administración</HomeSectionTitle>

						{/* Mismo par «indicador redondo + frase» que la banda del perfil, para
						    que las dos caras de este lugar se lean como la misma pieza. */}
						<div className="mt-6 flex items-center gap-5">
							<span
								className={`flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full text-[22px] font-bold ${
									total > 0 ? 'bg-[#233a4d] text-white' : 'bg-[#6E7F1E] text-white'
								}`}
							>
								{total > 0 ? total : <Check size={26} strokeWidth={3} aria-hidden />}
							</span>
							<p className="text-[15px] leading-relaxed text-black/[0.66]">
								{total > 0
									? `Tienes ${partes.join(' y ')} esperando revisión.`
									: 'No hay solicitudes ni alertas pendientes.'}
							</p>
						</div>

						<div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-3">
							<Link
								href="/admin"
								className="inline-flex items-center gap-2 rounded-[10px] bg-[#233a4d] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#233a4d]/90"
							>
								<ShieldCheck size={16} aria-hidden />
								Ir al panel
							</Link>
							<Link
								href="/investigadores"
								className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#354857] transition hover:underline"
							>
								Ver el directorio público
								<ArrowRight size={15} aria-hidden />
							</Link>
						</div>
					</div>

					<div className="flex flex-col gap-2.5">
						{ATAJOS_ADMIN.map((atajo) => (
							<HomeAtajoCard
								key={atajo.label}
								{...atajo}
								pendientes={
									atajo.href === '/admin/solicitudes'
										? solicitudes
										: atajo.href === '/admin/alertas'
											? alertas
											: undefined
								}
							/>
						))}
					</div>
				</div>
			</Reveal>
		</div>
	);
}
